import { useMemo } from 'react';
import CountUp from 'react-countup';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import { CUSTOMER_QUERY_SENTIMENT } from '@/types/customerConst';
import numberUtils from '@/utils/number';

import SentimentDoughnutChart from './SentimentDoughnutChart';

type Props = {
  dist: FetchCustomerSentimentDistRes | null;
  sentiment?: CUSTOMER_QUERY_SENTIMENT;
  selected: boolean;
  onClick: (sentiment?: CUSTOMER_QUERY_SENTIMENT) => void;
};

const SentimentSelectTabItem = (props: Props) => {
  const info = useMemo(() => {
    switch (props.sentiment) {
      case CUSTOMER_QUERY_SENTIMENT.HAPPY:
        return {
          title: 'Happy customers',
          count: props.dist?.happy || 0,
        };
      case CUSTOMER_QUERY_SENTIMENT.UNHAPPY:
        return {
          title: 'Unhappy customers',
          count: props.dist?.unhappy || 0,
        };
      default:
        return {
          title: 'All customers',
          count: props.dist?.count || 0,
        };
    }
  }, [
    props.dist?.count,
    props.dist?.happy,
    props.dist?.unhappy,
    props.sentiment,
  ]);

  return (
    <StyledCustomerSentimentCard
      onClick={() => props.onClick(props.sentiment)}
      selected={props.selected}
    >
      <Typo type="label2" weight="sb">
        {info.title}
      </Typo>
      <Flex j="space-between">
        <Heading type={props.sentiment ? 'h3' : 'h2'} weight="sb">
          <CountUp end={info.count} duration={1.4} />
        </Heading>

        {props.sentiment && (
          <div className="chart-container">
            <div className="chart-wrapper">
              <SentimentDoughnutChart
                current={info.count}
                total={props.dist?.count || 0}
                sentiment={props.sentiment}
              />

              <Typo weight="m" type="label3" className="chart-status">
                {Math.round(
                  numberUtils.getSharePercentage(info.count, props.dist?.count),
                )}
                %
              </Typo>
            </div>
          </div>
        )}
      </Flex>
    </StyledCustomerSentimentCard>
  );
};

const StyledCustomerSentimentCard = styled(VFlex).attrs<{ selected: boolean }>({
  j: 'space-between',
})`
  width: 200px;
  height: 120px;
  padding: 20px;
  border-radius: 8px;
  background-color: ${({ theme, selected }) =>
    selected ? theme.colors.primary100 : theme.colors.gray250};
  color: ${({ theme, selected }) =>
    selected ? theme.colors.FL100 : theme.colors.gray700};

  transition: all 0.2s;
  cursor: pointer;

  .chart-container {
    position: relative;
    width: 80px;
    height: 100%;

    .chart-wrapper {
      width: 80px;
      height: 80px;
      top: -36px;
      position: absolute;

      .chart-status {
        position: absolute;
        top: 36px;
        left: 50%;
        transform: translate(-50%, 100%);
      }
    }
  }
`;

export default SentimentSelectTabItem;
